import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useFusionAuth } from "@fusionauth/react-sdk";
import { useFetch } from "../../../services/hooks/useFetch";
import { deviceApiPrefix } from "../../../utils/main";
import { HTTPMethod } from "@fusionauth/typescript-client";

export default function ManufacturerSettingsPage() {
  const [editMode, setEditMode] = useState(false);

  const {
    create: createDMAlias,
    update: updateDMAlias,
    delete: deleteDMAlias,
  } = useFetch<undefined, Partial<DeviceManufacturerAlias>>();
  const {
    create: createDMDomain,
    update: updateDomainName,
    delete: deleteDMDomain,
  } = useFetch<undefined, Partial<DeviceManufacturerDomain>>();
  const {
    create: createDeviceAlias,
    update: updateDeviceAlias,
    delete: deleteDeviceAlias,
  } = useFetch<undefined, Partial<DeviceAlias>>();
  const { update: updateDeviceManufacturer } = useFetch<
    undefined,
    Partial<DeviceManufacturer>
  >();

  const { data: dmAliases, fetch: getDeviceManufacturerAliases } =
    useFetch<DeviceManufacturerAlias[]>();
  const { data: domains, fetch: getDeviceManufacturerDomains } =
    useFetch<DeviceManufacturerDomain[]>();
  const { data: deviceManufacturers, fetch: getDeviceManufacturers } =
    useFetch<DeviceManufacturer[]>();
  const { data: sources, fetch: getSources } = useFetch<Source[]>();

  const { data: devices, fetch: getDevicesByManufacturer } =
    useFetch<Device[]>();

  const { data: deviceAliases, fetch: getDeviceAliases } =
    useFetch<DeviceAlias[]>();

  const [selectedManufacturer, setSelectedManufacturer] = useState<
    Partial<DeviceManufacturer>
  >({});
  const [currentManufacturerName, setManufacturerName] = useState("N.A.");

  const [selectedDevice, setSelectedDevice] = useState<Partial<Device>>({});

  const [currentDMSearch, setCurrentDMSearch] = useState("");

  const [currentDeviceSearch, setCurrentDeviceSearch] = useState("");

  const [domainIndex, setDomainIndex] = useState(-1);
  const [domainName, setDomainName] = useState("N.A.");

  const [dmAliasIndex, setDMAliasIndex] = useState(-1);
  const [dmAliasName, setDMAliasName] = useState("N.A.");

  const [dmSourceIndex, setDMSourceIndex] = useState(-1);

  const [deviceAliasIndex, setDeviceAliasIndex] = useState(-1);
  const [deviceAliasName, setDeviceAliasName] = useState("N.A.");

  const [deviceSourceIndex, setDeviceSourceIndex] = useState(-1);

  const [domainsChanged, setDomainsChanged] = useState(false);
  const [dmAliasChanged, setDMAliasChanged] = useState(false);
  const [deviceAliasChanged, setDeviceAliasChanged] = useState(false);

  const [displayErrors, setDisplayErrors] = useState(false);

  const [deleteDMAliasDialogOpen, setDeleteDMAliasDialogOpen] = useState(false);
  const [deleteDomainDialogOpen, setDeleteDomainDialogOpen] = useState(false);
  const [deleteDeviceAliasDialogOpen, setDeleteDeviceAliasDialogOpen] =
    useState(false);

  const { userInfo } = useFusionAuth();
  const isWorkshop = userInfo?.roles?.includes("WORKSHOP");

  useEffect(() => {
    if (isWorkshop) {
      if (currentDMSearch !== "") {
        void getDeviceManufacturers(
          `${deviceApiPrefix}/devices/manufacturers?search=~${currentDMSearch}~`,
        );
      } else {
        void getDeviceManufacturers(
          `${deviceApiPrefix}/devices/manufacturers?search=~~`,
        );
      }
    } else {
      void getDeviceManufacturers(
        `${deviceApiPrefix}/devices/manufacturers/company/`,
      );
    }
  }, [isWorkshop, getDeviceManufacturers, currentDMSearch]);

  useEffect(() => {
    if (selectedManufacturer?.id) {
      void getDevicesByManufacturer(
        `${deviceApiPrefix}/devices/manufacturers/${selectedManufacturer?.id}/all?search=${currentDeviceSearch}`,
      );
    }
  }, [currentDeviceSearch, getDevicesByManufacturer, selectedManufacturer?.id]);

  useEffect(() => {
    if (selectedManufacturer.id) {
      const id = selectedManufacturer.id;
      void getDeviceManufacturerDomains(
        `${deviceApiPrefix}/devices/manufacturers/${id}/domains`,
      );

      setDomainIndex(-1);
      setDomainName("N.A.");

      if (domainsChanged) setDomainsChanged(false);
    }
  }, [
    selectedManufacturer,
    deviceManufacturers,
    domainsChanged,
    getDeviceManufacturerDomains,
  ]);

  useEffect(() => {
    if (selectedManufacturer.id) {
      const id = selectedManufacturer.id;

      void getDeviceManufacturerAliases(
        `${deviceApiPrefix}/devices/manufacturers/${id}/aliases`,
      );

      setDMAliasIndex(-1);
      setDMAliasName("N.A.");

      if (dmAliasChanged) setDMAliasChanged(false);
    }
  }, [
    selectedManufacturer,
    deviceManufacturers,
    dmAliasChanged,
    getDeviceManufacturerAliases,
  ]);

  useEffect(() => {
    if (selectedDevice.id) {
      const id = selectedDevice.id;

      void getDeviceAliases(`${deviceApiPrefix}/devices/${id}/aliases`);

      setDeviceAliasIndex(-1);
      setDeviceAliasName("N.A.");

      if (deviceAliasChanged) setDeviceAliasChanged(false);
    }
  }, [
    selectedDevice,
    devices,
    deviceAliasChanged,
    setDeviceAliasChanged,
    getDeviceAliases,
  ]);

  useEffect(() => {
    void getSources(`${deviceApiPrefix}/sources/`);
  }, [getSources]);

  useEffect(() => {
    if (selectedManufacturer && selectedManufacturer.name) {
      setManufacturerName(selectedManufacturer.name);
    } else {
      setManufacturerName("N.A.");
    }
  }, [selectedManufacturer]);

  const getSourceIndex = function (sourceId: string | null) {
    let sourceIndex = -1;
    if (sourceId === null) {
      return sourceIndex;
    }
    sources?.forEach((source, index) => {
      if (source.id === sourceId) {
        sourceIndex = index;
      }
    });
    return sourceIndex;
  };

  return (
    <Grid container direction={"column"} className="company-settings-item">
      <div className="company-settings-item">
        <Grid>
          <Typography variant="h4">Manufacturer Settings</Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">
            Information about the manufacturer.
          </Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">* Required fields</Typography>
        </Grid>
        <br />
        <Grid>
          <Autocomplete
            disablePortal
            options={deviceManufacturers ? deviceManufacturers : []}
            sx={{ width: "50 ch" }}
            getOptionLabel={(option) => {
              if (option.name) {
                if (option.dunsNumber && option.dunsNumber !== "") {
                  return option.name + " (" + option.dunsNumber + ")";
                } else {
                  return option.name;
                }
              } else {
                return "";
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Current Device Manufacturer" />
            )}
            inputValue={currentDMSearch}
            onInputChange={(event, value) => {
              if (event === null) {
                setCurrentDMSearch("");
              } else {
                setCurrentDMSearch(value);
              }
            }}
            value={selectedManufacturer.id ? selectedManufacturer : null}
            onChange={(_event, value) => {
              if (value === null) {
                setSelectedManufacturer({});
                setCurrentDMSearch("");
              } else {
                setSelectedManufacturer(value);
                if (value.name) {
                  if (value.dunsNumber) {
                    setCurrentDMSearch(
                      value.name + " (" + value.dunsNumber + ")",
                    );
                  } else {
                    setCurrentDMSearch(value.name);
                  }
                } else {
                  setCurrentDMSearch("");
                }
              }
            }}
            isOptionEqualToValue={(option, value) => {
              if (value === null) {
                return true;
              }

              if (option.id && value.id) {
                return option.id === value.id;
              }

              return false;
            }}
          />
        </Grid>
        <br />
        <Grid>
          {editMode ? (
            <TextField
              required
              variant="outlined"
              label="Device Manufacturer Name"
              className="settings-text-input"
              sx={{ width: "50ch" }}
              value={currentManufacturerName}
              error={displayErrors && currentManufacturerName === ""}
              helperText={
                displayErrors && currentManufacturerName === ""
                  ? "This field is required"
                  : ""
              }
              slotProps={{
                input: {
                  readOnly: !editMode || !selectedManufacturer.id,
                },
              }}
              onChange={(event) => {
                setManufacturerName(event.target.value);
              }}
            />
          ) : (
            <>
              <Typography variant="caption">
                Device Manufacturer Name *
              </Typography>
              <Typography variant="body1">{currentManufacturerName}</Typography>
            </>
          )}
        </Grid>
        <br />
        <Grid>
          <InputLabel shrink htmlFor="select-domain">
            Current Device Manufacturer Domain
          </InputLabel>
          <Select
            value={domainIndex}
            defaultValue={-1}
            onChange={(event) => {
              setDomainIndex(event.target.value);

              if (event.target.value === -1) {
                setDomainName("N.A.");
              } else if (event.target.value === -2) {
                setDomainName("");
              } else {
                setDomainName(
                  domains ? domains[event.target.value].domain : "",
                );
              }
            }}
            inputProps={{
              id: "select-domain",
            }}
            variant="outlined"
            fullWidth
          >
            <MenuItem value={-1}>None</MenuItem>
            {domains?.map((json, index) => {
              return (
                <MenuItem key={index} value={index}>
                  {`${json.id} (${json.domain})`}
                </MenuItem>
              );
            })}
            {selectedManufacturer.id && editMode && (
              <MenuItem value={-2}>Add New Domain</MenuItem>
            )}
          </Select>
        </Grid>
        <br />
        {editMode ? (
          <Grid>
            <TextField
              required
              variant="outlined"
              label="Device Manufacturer Domain Name"
              className="settings-text-input"
              sx={{ width: "50ch" }}
              value={domainName}
              error={displayErrors && domainName === ""}
              helperText={
                displayErrors && domainName === ""
                  ? "This field is required"
                  : ""
              }
              slotProps={{
                input: {
                  readOnly: !editMode || domainIndex === -1,
                },
              }}
              onChange={(event) => {
                setDomainName(event.target.value);
              }}
            />
          </Grid>
        ) : (
          <>
            <Typography variant="caption">
              Device Manufacturer Domain Name *
            </Typography>
            <Typography variant="body1" sx={{ width: "50ch" }}>
              {domainName}
            </Typography>
          </>
        )}
        {domainIndex !== -1 && domainIndex !== -2 && editMode && (
          <>
            <br />
            <Grid>
              <Button
                variant="contained"
                onClick={() => setDeleteDomainDialogOpen(true)}
              >
                Delete
              </Button>
            </Grid>
            <Dialog
              open={deleteDomainDialogOpen}
              onClose={() => setDeleteDomainDialogOpen(false)}
            >
              <DialogTitle>Delete Device Manufacturer Domain</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure that you would like delete this device
                  manufacturer domain?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteDomainDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    deleteDMDomain({
                      path: `${deviceApiPrefix}/devices/manufacturers/domains/${domains![domainIndex].id}`,
                    }).then(() => {
                      setDomainsChanged(true);
                      setDomainIndex(-1);
                      setDomainName("N.A.");
                      setEditMode(false);
                    });
                    setDeleteDomainDialogOpen(false);
                  }}
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
        <br />
        <Grid>
          <InputLabel shrink htmlFor="select-alias">
            Current Device Manufacturer Alias
          </InputLabel>
          <Select
            value={dmAliasIndex}
            defaultValue={-1}
            onChange={(event) => {
              setDMAliasIndex(event.target.value);
              if (event.target.value === -1) {
                setDMAliasName("N.A.");
                setDMSourceIndex(-1);
              } else if (event.target.value === -2) {
                setDMAliasName("");
                setDMSourceIndex(-1);
              } else {
                setDMAliasName(
                  dmAliases ? dmAliases[event.target.value].alias : "",
                );
                setDMSourceIndex(
                  getSourceIndex(
                    dmAliases ? dmAliases[event.target.value].sourceId : "",
                  ),
                );
              }
            }}
            inputProps={{
              id: "select-alias",
            }}
            variant="outlined"
            fullWidth
          >
            <MenuItem value={-1}>None</MenuItem>
            {dmAliases?.map((json, index) => {
              return (
                <MenuItem key={index} value={index}>
                  {`${json.id} (${json.alias})`}
                </MenuItem>
              );
            })}
            {selectedManufacturer.id && editMode && (
              <MenuItem value={-2}>Add New Alias</MenuItem>
            )}
          </Select>
        </Grid>
        <br />
        {editMode ? (
          <Grid>
            <TextField
              required
              variant="outlined"
              label="Device Manufacturer Alias Name"
              className="settings-text-input"
              sx={{ width: "50ch" }}
              value={dmAliasName}
              error={displayErrors && dmAliasName === ""}
              helperText={
                displayErrors && dmAliasName === ""
                  ? "This field is required"
                  : ""
              }
              slotProps={{
                input: {
                  readOnly: !editMode || dmAliasIndex === -1,
                },
              }}
              onChange={(event) => {
                setDMAliasName(event.target.value);
              }}
            />
          </Grid>
        ) : (
          <>
            <Typography variant="caption">
              Device Manufacturer Alias Name *
            </Typography>
            <Typography variant="body1" sx={{ width: "50ch" }}>
              {dmAliasName}
            </Typography>
          </>
        )}
        <br />
        {editMode ? (
          <Grid>
            <InputLabel shrink htmlFor="select-source">
              Device Manufacturer Alias Source
            </InputLabel>
            <Select
              value={dmSourceIndex}
              defaultValue={-1}
              onChange={(event) => {
                setDMSourceIndex(event.target.value);
              }}
              inputProps={{
                id: "select-source",
                readOnly: !editMode,
              }}
              variant="outlined"
              fullWidth
            >
              <MenuItem value={-1}>None</MenuItem>
              {sources?.map((json, index) => {
                return (
                  <MenuItem key={index} value={index}>
                    {json.name}
                  </MenuItem>
                );
              })}
            </Select>
          </Grid>
        ) : (
          <>
            <Typography variant="caption">
              Device Manufacturer Alias Source
            </Typography>
            <Typography variant="body1" sx={{ width: "50ch" }}>
              {sources && sources[dmSourceIndex]
                ? sources[dmSourceIndex].name
                : "None"}
            </Typography>
          </>
        )}
        {dmAliasIndex !== -1 && dmAliasIndex !== -2 && editMode && (
          <>
            <br />
            <Grid>
              <Button
                variant="contained"
                onClick={() => setDeleteDMAliasDialogOpen(true)}
              >
                Delete
              </Button>
            </Grid>
            <Dialog
              open={deleteDMAliasDialogOpen}
              onClose={() => setDeleteDMAliasDialogOpen(false)}
            >
              <DialogTitle>Delete Device Manufacturer Alias</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure that you would like delete this device
                  manufacturer alias?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteDMAliasDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    void deleteDMAlias({
                      path: `${deviceApiPrefix}/devices/manufacturers/${selectedManufacturer.id}/aliases/${dmAliases![dmAliasIndex].id}`,
                      postResponseAction: () => {
                        setDMAliasChanged(true);
                        setDMAliasIndex(-1);
                        setDMAliasName("N.A.");
                        setDMSourceIndex(-1);
                        setEditMode(false);
                      },
                    });
                  }}
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
        <br />
        <Grid>
          <Autocomplete
            disablePortal
            options={devices ? devices : []}
            sx={{ width: "50 ch" }}
            getOptionLabel={(option) => {
              if (option.description) {
                return option.description;
              } else {
                return "";
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Current Device" />
            )}
            inputValue={currentDeviceSearch}
            onInputChange={(event, value) => {
              if (event === null) {
                setCurrentDeviceSearch("");
              } else {
                setCurrentDeviceSearch(value);
              }
            }}
            value={selectedDevice.id ? selectedDevice : null}
            onChange={(_event, value) => {
              if (value === null) {
                setSelectedDevice({});
                setCurrentDeviceSearch("");
              } else {
                setSelectedDevice(value);
                if (value.description) {
                  setCurrentDeviceSearch(value.description);
                } else {
                  setCurrentDeviceSearch("");
                }
              }
            }}
            isOptionEqualToValue={(option, value) => {
              if (value === null) {
                return true;
              }

              if (option.id && value.id) {
                return option.id === value.id;
              }

              return false;
            }}
          />
        </Grid>
        <br />
        <Grid>
          <InputLabel shrink htmlFor="select-alias">
            Current Device Alias
          </InputLabel>
          <Select
            value={deviceAliasIndex}
            defaultValue={-1}
            onChange={(event) => {
              setDeviceAliasIndex(event.target.value);
              if (event.target.value === -1) {
                setDeviceAliasName("N.A.");
                setDeviceSourceIndex(-1);
              } else if (event.target.value === -2) {
                setDeviceAliasName("");
                setDeviceSourceIndex(-1);
              } else {
                setDeviceAliasName(
                  deviceAliases ? deviceAliases[event.target.value].alias : "",
                );
                setDeviceSourceIndex(
                  getSourceIndex(
                    deviceAliases
                      ? deviceAliases[event.target.value].sourceId
                      : "",
                  ),
                );
              }
            }}
            inputProps={{
              id: "select-alias",
            }}
            variant="outlined"
            fullWidth
          >
            <MenuItem value={-1}>None</MenuItem>
            {deviceAliases?.map((json, index) => {
              return (
                <MenuItem key={index} value={index}>
                  {`${json.id} (${json.alias})`}
                </MenuItem>
              );
            })}
            {selectedDevice.id && editMode && (
              <MenuItem value={-2}>Add New Alias</MenuItem>
            )}
          </Select>
        </Grid>
        <br />
        {editMode ? (
          <Grid>
            <TextField
              required
              variant="outlined"
              label="Device Alias Name"
              className="settings-text-input"
              sx={{ width: "50ch" }}
              value={deviceAliasName}
              error={displayErrors && deviceAliasName === ""}
              helperText={
                displayErrors && deviceAliasName === ""
                  ? "This field is required"
                  : ""
              }
              slotProps={{
                input: {
                  readOnly: !editMode || deviceAliasIndex === -1,
                },
              }}
              onChange={(event) => {
                setDeviceAliasName(event.target.value);
              }}
            />
          </Grid>
        ) : (
          <>
            <Typography variant="caption">Device Alias Name *</Typography>
            <Typography variant="body1" sx={{ width: "50ch" }}>
              {deviceAliasName}
            </Typography>
          </>
        )}
        <br />
        {editMode ? (
          <Grid>
            <InputLabel shrink htmlFor="select-source">
              Device Alias Source
            </InputLabel>
            <Select
              value={deviceSourceIndex}
              defaultValue={-1}
              onChange={(event) => {
                setDeviceSourceIndex(event.target.value);
              }}
              inputProps={{
                id: "select-source",
                readOnly: !editMode,
              }}
              variant="outlined"
              fullWidth
            >
              <MenuItem value={-1}>None</MenuItem>
              {sources?.map((json, index) => {
                return (
                  <MenuItem key={index} value={index}>
                    {json.name}
                  </MenuItem>
                );
              })}
            </Select>
          </Grid>
        ) : (
          <>
            <Typography variant="caption">
              Device Manufacturer Alias Source
            </Typography>
            <Typography variant="body1" sx={{ width: "50ch" }}>
              {sources && sources[deviceSourceIndex]
                ? sources[deviceSourceIndex].name
                : "None"}
            </Typography>
          </>
        )}
        {deviceAliasIndex !== -1 && deviceAliasIndex !== -2 && editMode && (
          <>
            <br />
            <Grid>
              <Button
                variant="contained"
                onClick={() => setDeleteDeviceAliasDialogOpen(true)}
              >
                Delete
              </Button>
            </Grid>
            <Dialog
              open={deleteDeviceAliasDialogOpen}
              onClose={() => setDeleteDeviceAliasDialogOpen(false)}
            >
              <DialogTitle>Delete Device Alias</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure that you would like delete this device alias?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteDeviceAliasDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    void deleteDeviceAlias({
                      path: `${deviceApiPrefix}/devices/${selectedDevice.id}/aliases/${deviceAliases![deviceAliasIndex].id}`,
                      postResponseAction: () => {
                        setDeviceAliasChanged(true);
                        setDeviceAliasIndex(-1);
                        setDeviceAliasName("N.A.");
                        setDeviceSourceIndex(-1);
                        setEditMode(false);
                      },
                    });
                  }}
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
        <br />
        <Grid container justifyContent={"right"}>
          {(editMode && (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setManufacturerName(selectedManufacturer.name || "");
                  if (dmAliasIndex !== -1) {
                    if (dmAliasIndex === -2) {
                      setDMAliasIndex(-1);
                      setDMAliasName("N.A.");
                    } else {
                      setDMAliasName(
                        dmAliases ? dmAliases[dmAliasIndex].alias : "",
                      );
                    }
                  }
                  if (domainIndex !== -1) {
                    if (domainIndex === -2) {
                      setDomainIndex(-1);
                      setDomainName("N.A.");
                    } else {
                      setDomainName(domains ? domains[domainIndex].domain : "");
                    }
                  }
                  setDisplayErrors(false);
                  setEditMode(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="update-button"
                sx={{ marginLeft: "5px" }}
                onClick={() => {
                  if (
                    currentManufacturerName === "" ||
                    domainName === "" ||
                    dmAliasName === ""
                  ) {
                    setDisplayErrors(true);
                    return;
                  }

                  if (selectedDevice.id && deviceAliasName === "") {
                    setDisplayErrors(true);
                    return;
                  }

                  if (
                    selectedManufacturer.id &&
                    currentManufacturerName !== selectedManufacturer.name
                  ) {
                    const id = selectedManufacturer.id;

                    void updateDeviceManufacturer({
                      path: `${deviceApiPrefix}/devices/manufacturers/update/${id}`,
                      body: { name: currentManufacturerName },
                    });
                  }

                  if (
                    domains &&
                    domains[domainIndex] &&
                    domainName !== domains[domainIndex].domain
                  ) {
                    const domainId = domains[domainIndex].id;
                    domains[domainIndex].domain = domainName;

                    void updateDomainName({
                      path: `${deviceApiPrefix}/devices/manufacturers/domains/${domainId}`,
                      body: domains[domainIndex],
                      postResponseAction: () => {
                        setDomainsChanged(true);
                      },
                    });
                  } else if (domainIndex === -2) {
                    void createDMDomain({
                      path: `${deviceApiPrefix}/devices/manufacturers/domains/`,
                      body: {
                        deviceManufacturerId: selectedManufacturer.id,
                        domain: domainName,
                      },
                      postResponseAction: () => {
                        setDomainsChanged(true);
                        setDomainIndex(-1);
                        setDomainName("N.A.");
                      },
                    });
                  }

                  if (
                    dmAliases &&
                    dmAliases[dmAliasIndex] &&
                    dmAliasName !== dmAliases[dmAliasIndex].alias
                  ) {
                    const dmId = selectedManufacturer.id;
                    const aliasId = dmAliases[dmAliasIndex].id;
                    void updateDMAlias({
                      path: `${deviceApiPrefix}/devices/manufacturers/${dmId}/aliases/${aliasId}`,
                      body: { alias: dmAliasName },
                      postResponseAction: () => {
                        setDMAliasChanged(true);
                      },
                    });
                  } else if (dmAliasIndex === -2) {
                    let sourceId = null;

                    if (sources && sources[dmSourceIndex]) {
                      sourceId = sources[dmSourceIndex].id;
                    }

                    void createDMAlias({
                      path: `${deviceApiPrefix}/devices/manufacturers/${selectedManufacturer.id}/aliases`,
                      body: {
                        deviceManufacturerId: selectedManufacturer.id,
                        sourceId: sourceId,
                        alias: dmAliasName,
                      },
                      postResponseAction: () => {
                        setDMAliasChanged(true);
                        setDMAliasIndex(-1);
                        setDMAliasName("N.A.");
                        setDMSourceIndex(-1);
                      },
                    });
                  }

                  if (
                    deviceAliases &&
                    deviceAliases[deviceAliasIndex] &&
                    deviceAliasName !== deviceAliases[deviceAliasIndex].alias
                  ) {
                    const deviceId = selectedDevice.id;
                    const aliasId = deviceAliases[deviceAliasIndex].id;
                    void updateDeviceAlias({
                      path: `${deviceApiPrefix}/devices/${deviceId}/aliases/${aliasId}`,
                      body: { alias: deviceAliasName },
                      postResponseAction: () => {
                        setDeviceAliasChanged(true);
                      },
                    });
                  } else if (deviceAliasIndex === -2) {
                    let sourceId = null;

                    if (sources && sources[deviceSourceIndex]) {
                      sourceId = sources[deviceSourceIndex].id;
                    }

                    void createDeviceAlias({
                      path: `${deviceApiPrefix}/devices/${selectedDevice.id}/aliases`,
                      body: {
                        deviceId: selectedDevice.id,
                        sourceId: sourceId,
                        alias: deviceAliasName,
                      },
                      postResponseAction: () => {
                        setDeviceAliasChanged(true);
                        setDeviceAliasIndex(-1);
                        setDeviceAliasName("N.A.");
                        setDeviceSourceIndex(-1);
                      },
                    });
                  }

                  if (
                    dmAliases &&
                    dmAliases[dmAliasIndex] &&
                    sources &&
                    sources[dmSourceIndex] &&
                    sources[dmSourceIndex].id !==
                      dmAliases[dmAliasIndex].sourceId
                  ) {
                    const manufacturerId = selectedManufacturer;
                    const aliasId = dmAliases[dmAliasIndex].id;
                    void updateDMAlias({
                      path: `${deviceApiPrefix}/devices/manufacturers/${manufacturerId}/aliases/${aliasId}`,
                      body: { sourceId: sources[dmSourceIndex].id },
                      method: HTTPMethod.POST,
                    });
                  }

                  if (
                    deviceAliases &&
                    deviceAliases[deviceAliasIndex] &&
                    sources &&
                    sources[deviceSourceIndex] &&
                    sources[deviceSourceIndex].id !==
                      deviceAliases[deviceAliasIndex].sourceId
                  ) {
                    const deviceId = selectedDevice.id;
                    const aliasId = deviceAliases[deviceAliasIndex].id;
                    void updateDeviceAlias({
                      path: `${deviceApiPrefix}/devices/${deviceId}/aliases/${aliasId}`,
                      body: { sourceId: sources[deviceSourceIndex].id },
                      method: HTTPMethod.PUT,
                    });
                  }

                  setEditMode(false);
                }}
              >
                Update
              </Button>
            </>
          )) || (
            <Button variant="contained" onClick={() => setEditMode(true)}>
              Edit
            </Button>
          )}
        </Grid>
      </div>
    </Grid>
  );
}
